import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flex, PrimaryButton, Panel, ToolkitFeatureFlag, SecondaryButton } from '@cko/primitives';
import { IconActionCross } from '@cko/icons';
import { SelectedMerchant, trackEvent, useCurrentClientManager } from '@cko/dashboard-shared';

import { AccountSelectorBreadCrumb } from '../AccountSelectorBreadCrumb';
import { useClientsQuery } from '../../hooks';
import { AccountSelectorSearch, AccountSelectorSearchRef } from '../AccountSelectorSearch';
import { AccountSelectorNewViewSelector } from '../AccountSelectorNewViewSelector';
import {
  AccountSelectorNoResults,
  AccountSelectorLoading,
  AccountSelectorQueryError,
} from './AccountSelectorDialogStateViews';

const limit = 25;

const DialogContainer = styled(Panel)`
  z-index: 10000;
  border-radius: var(--border-r-stamp);
  margin: var(--layout-spoon);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 540px;
  max-height: 540px;
  min-width: 688px;
  flex-direction: column;
`;

const DialogHeader = styled(Flex)`
  padding: var(--layout-soybean) var(--layout-spoon);
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-bottom: var(--border-b-seed) solid var(--base-border-secondary);
  min-height: 50px;
`;

const DialogContent = styled(Flex)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: var(--layout-saxophone);
  flex: 1 0 0;
  align-self: stretch;
`;

const DialogFooter = styled(Flex)`
  flex-direction: row-reverse;
  padding: var(--layout-soybean) var(--layout-spoon);
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-top: var(--border-b-seed) solid var(--base-border-secondary);
`;

const Actions = styled(Flex)`
  gap: var(--layout-stamp);
`;

const CloseIconContainer = styled(Flex)`
  cursor: pointer;
`;

interface AccountSelectorDialogProps {
  usersApiBasePath: string;
  closeMenu: () => void;
}

export const AccountSelectorDialog = ({
  usersApiBasePath,
  closeMenu,
}: AccountSelectorDialogProps) => {
  const currentView = useCurrentClientManager();

  const updatedViewFromCurrent = () => {
    const view: SelectedMerchant = {
      client: currentView.client,
      entity: currentView.entity,
      segment: currentView.segment,
      availableEntities: currentView.availableEntities,
      availableSegments: currentView.availableSegments,
    };

    return view;
  };

  const [searchString, setSearchString] = useState<string | undefined>(undefined);
  const [searchFieldVisible, setSearchFieldVisible] = useState<boolean>(false);
  const accountSelectorSearchRef = useRef<AccountSelectorSearchRef>();

  const resetSearchField = () => {
    accountSelectorSearchRef.current?.resetSearch();
  };

  const [updatedView, setUpdatedView] = useState<SelectedMerchant | undefined>(
    updatedViewFromCurrent(),
  );

  const onSubmitSearch = (newSearchTerm: string) => {
    setUpdatedView(undefined);
    const trimmedSearchTerm = newSearchTerm.trim();
    trackEvent('AccountSelectorSearch', { searchTerm: trimmedSearchTerm });
    setSearchString(trimmedSearchTerm);
  };

  const onResetSearch = () => {
    setUpdatedView(updatedViewFromCurrent());
    setSearchString(undefined);
  };

  const {
    isLoading,
    isSuccess,
    isError,
    refetch,
    clients,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useClientsQuery(usersApiBasePath, searchString || '', limit);

  const [isSubmittingUpdatedView, setIsSubmittingUpdatedView] = useState<boolean>(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);

  const setInitialClientSegments = useCallback(async () => {
    if (currentView.client && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [currentView.client, initialLoadComplete]);

  useEffect(() => {
    setInitialClientSegments();
  }, [setInitialClientSegments]);

  useEffect(() => {
    if (hasNextPage) {
      setSearchFieldVisible(true);
    }
  }, [hasNextPage, setSearchFieldVisible]);

  const onSubmitUpdatedView = async () => {
    trackEvent('AccountSelectorViewUpdated', {
      // eslint-disable-next-line no-nested-ternary
      selectedLevel: updatedView?.segment ? 'segment' : updatedView?.entity ? 'entity' : 'client',
    });
    setIsSubmittingUpdatedView(true);
    if (updatedView) {
      const updateSucceeded = await currentView.actions.changeView(updatedView);
      if (updateSucceeded) {
        closeMenu();
      }
    } else {
      closeMenu();
    }
    setIsSubmittingUpdatedView(false);
  };

  const onSetNewView = async (newView: SelectedMerchant) => {
    setUpdatedView(newView);
  };

  const onCloseMenu = () => {
    setUpdatedView(undefined);
    closeMenu();
  };

  return (
    <ToolkitFeatureFlag flag={'v10'}>
      <DialogContainer
        data-testid="account-selector-dialog"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <CloseIconContainer onClick={onCloseMenu}>
            <IconActionCross />
          </CloseIconContainer>

          {searchFieldVisible && (
            <AccountSelectorSearch
              ref={accountSelectorSearchRef}
              onSubmitSearch={onSubmitSearch}
              onResetSearch={onResetSearch}
            />
          )}
        </DialogHeader>
        <DialogContent>
          {(isLoading || !initialLoadComplete) && <AccountSelectorLoading />}
          {isError && <AccountSelectorQueryError refetch={refetch} />}
          {isSuccess && clients.length === 0 && (
            <AccountSelectorNoResults resetSearch={resetSearchField} />
          )}
          {isSuccess && initialLoadComplete && clients.length > 0 && (
            <AccountSelectorNewViewSelector
              newView={updatedView}
              existingView={currentView}
              clients={clients}
              setNewView={onSetNewView}
              fetchMoreClients={fetchNextPage}
              isFetchingMoreClients={isFetchingNextPage}
              hasMoreClients={hasNextPage}
            />
          )}
        </DialogContent>
        <DialogFooter>
          <Actions>
            <SecondaryButton onClick={closeMenu}>Cancel</SecondaryButton>
            <PrimaryButton
              disabled={!updatedView}
              isLoading={isSubmittingUpdatedView}
              onClick={onSubmitUpdatedView}
            >
              Switch view
            </PrimaryButton>
          </Actions>
          <AccountSelectorBreadCrumb updatedView={updatedView} />
        </DialogFooter>
      </DialogContainer>
    </ToolkitFeatureFlag>
  );
};
